import { useCallback, useState } from "react";
import styled from "styled-components";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Heading, Link } from "@viagogo/core/ui";
import { Feature, RemoveFeature, useSimpleFeatureTernary } from "@viagogo/feature";
import { Content } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useSpotifyConnect } from "src/website-apps/responsive/stubhub-home/components/connect-to-spotify/SpotifyConnectProvider";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { renderCommonCategoryCard } from "src/website-apps/shared/components/common-category-card";
import { HOME_CAROUSEL_IDS } from "src/website-apps/shared/components/responsive-carousel/constants";
import { ResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/ResponsiveCarousel";
import {
  carouselHeaderFontStyleFix,
  carouselHeaderGridSpacingFix,
} from "src/website-apps/shared/components/tm-home-fix-header-sizing/util";
import { useCarouselDimensions } from "src/website-apps/shared/hooks/useCarouselDimensions";

/** "From your music library" */
const HEADER_CONTENT_ID = "cid$#3649008#";
/** Your music library, live near you */
const HEADER_LOCALIZED_CONTENT_ID = "cid$#3819227#";
const MANAGE_BUTTON_CONTENT_ID = "cid$#3295715#";
const MANAGE_TOOLTIP_CONTENT_ID = "cid$#3304904#";
const MANAGE_TOOLTIP_LINK_CONTENT_ID = "cid$#3304907#";
const SPOTIFY_HELP_ARTICLE_URL = "https://support.spotify.com/us/article/spotify-on-other-apps/";

const CarouselWidthController = ({ children }) => (
  <div style={{ overflowX: "hidden", padding: "0 20px", margin: "0 -20px" }}>{children}</div>
);

const ManageTooltip = styled.div`
  position: relative;
  display: inline-block;
`;

const HelpCenterLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary400};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const ManageTooltipTextContainer = styled.div`
  width: 250px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3, 4)};
  position: absolute;
  font-size: 14px;
  z-index: 2;
  margin-top: ${({ theme }) => theme.spacing(2)};
  right: ${({ theme }) => theme.spacing(0)};
  box-shadow: rgba(0, 0, 0, 0.3) 0 4px 10px;
`;

const ManageTooltipText = styled.p`
  padding-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Container = styled.div`
  display: grid;
  grid: auto / 1fr;
  gap: ${({ theme }) => theme.spacing(4)};
  ${carouselHeaderGridSpacingFix};
`;

const Title = styled(Heading)`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2em;
  color: ${({ theme }) => theme.colors.textDarker};
  margin: 0;
  @media only screen and (min-width: ${({ theme }) => theme.media.breakpoints.md}px) {
    font-size: 20px;
  }
  ${carouselHeaderFontStyleFix};
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const ManageConnectionButton = styled.button`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`;

export const TopSpotifyArtistCarousel = ({ initItems, size }) => {
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const { spotifyAccountConnected } = useSpotifyConnect();
  const [tooltipActive, setTooltipActive] = useState(false);
  const toggleTooltip = useCallback(() => setTooltipActive((prev) => !prev), []);
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const { carouselSize, imageSize, gapPx } = useCarouselDimensions(size);
  const hasItems = initItems?.items?.length > 0;
  const isGridCardRefactorEnabled =
    useSimpleFeatureTernary(FeatureSetEnum.UseGridCardBaseComponentRefactor, false, true) ?? false;

  return (
    <Container>
      {spotifyAccountConnected && hasItems ? (
        <>
          <RemoveFeature
            id={FeatureSetEnum.TMGlobalSpotifyBannerWithStatus}
            feature={() => (
              <TitleContainer>
                <Title>
                  <Feature
                    id={FeatureSetEnum.TMHomeLocalizeArtistCarousel}
                    control={() => <Content id={HEADER_CONTENT_ID} />}
                    variant={() => <Content id={HEADER_LOCALIZED_CONTENT_ID} />}
                  />
                </Title>
                <ManageTooltip>
                  <ManageConnectionButton onClick={toggleTooltip}>
                    <Content id={MANAGE_BUTTON_CONTENT_ID} />
                  </ManageConnectionButton>
                  {tooltipActive && (
                    <ManageTooltipTextContainer>
                      <ManageTooltipText>
                        <Content id={MANAGE_TOOLTIP_CONTENT_ID} />
                      </ManageTooltipText>
                      <HelpCenterLink url={SPOTIFY_HELP_ARTICLE_URL} newTab>
                        <Content id={MANAGE_TOOLTIP_LINK_CONTENT_ID} />
                      </HelpCenterLink>
                    </ManageTooltipTextContainer>
                  )}
                </ManageTooltip>
              </TitleContainer>
            )}
          />
          <Feature
            id={FeatureSetEnum.TMGlobalSpotifyBannerWithStatus}
            control={() => null}
            variant={() => (
              <Title>
                <Feature
                  id={FeatureSetEnum.TMHomeLocalizeArtistCarousel}
                  control={() => <Content id={HEADER_CONTENT_ID} />}
                  variant={() => <Content id={HEADER_LOCALIZED_CONTENT_ID} />}
                />
              </Title>
            )}
          />
          <CarouselWidthController>
            <ResponsiveCarousel
              useFlex={isGridCardRefactorEnabled}
              size={carouselSize}
              hidePaginationButtons={isMobile}
              items={initItems?.items ?? []}
              hasHoverEffect={!isMobile}
              renderItem={(item, index) =>
                renderCommonCategoryCard(item, {
                  size: isMobile ? "mobile" : "default",
                  index,
                  imageProps: { size: imageSize },
                  localEventsProps:
                    item !== null && item !== undefined
                      ? {
                          formattedLocalEventsDates: item.formattedLocalEventsDates,
                          availableLocalEvents: item.availableLocalEvents,
                        }
                      : null,
                  onClick: () => {
                    storePageEvent(
                      PE_TYPE.CLICK,
                      PE_KEYS.SPOTIFY_CAROUSEL_CLICK,
                      JSON.stringify({
                        Index: index,
                        CategoryId: item.categoryId,
                        AnonymousId: appContext.anonymousId,
                      }),
                    );
                  },
                })
              }
              carouselId={HOME_CAROUSEL_IDS.spotifyYourMusicCarousel}
              analyticsCarouselId={CarouselId.SpotifyArtists}
              analyticsItemType={CarouselItemType.Artist}
            />
          </CarouselWidthController>
        </>
      ) : null}
    </Container>
  );
};
